import { useState } from 'react';

/**
 * Custom hook för att hantera offert-actions (skicka offert, etc.)
 * 
 * @param {Object} offert - Offert-objektet
 * @param {Object} procurement - Procurement-objektet
 * @param {Function} setOffert - Funktion för att uppdatera offert
 * @param {Function} onSuccess - Callback för att visa success modal
 * @param {Function} onError - Callback för att visa error modal
 * @returns {Object} - State och funktioner för offert-actions
 */
const useOffertActions = (offert, procurement, setOffert, onSuccess, onError) => {
  const [sending, setSending] = useState(false);

  // Skicka offert till kund
  const handleSendOffert = async (changeSummary = '', customerMessage = '') => {
    if (!offert?._id) {
      if (onError) {
        onError({
          title: 'Fel',
          message: 'Ingen offert att skicka',
          type: 'error'
        });
      }
      return;
    }

    setSending(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offert._id}/send`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ changeSummary, customerMessage })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Kunde inte skicka offert');
      }

      const data = await response.json();
      
      if (data.success) {
        // Uppdatera offert med nya data
        setOffert(data.data);
        
        // Uppdatera procurement status till "Offert" när första offerten skickas
        try {
          if (procurement?._id) {
            const statusResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/procurement/${procurement._id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                status: 'Offert'
              })
            });
            
            if (!statusResponse.ok) {
              console.warn('⚠️ Kunde inte uppdatera procurement status');
            }
          }
        } catch (statusError) {
          console.warn('⚠️ Fel vid uppdatering av procurement status:', statusError);
        }
        
        // Visa framgångsmeddelande
        if (onSuccess) {
          onSuccess({
            title: 'Offert skickad!',
            message: `Offerten har skickats till kunden. Kunden har fått ett e-postmeddelande med länk till offerten.`,
            type: 'success'
          });
        }
      } else {
        throw new Error(data.message || 'Kunde inte skicka offert');
      }
    } catch (err) {
      console.error('❌ Fel vid skickande av offert:', err);
      if (onError) {
        onError({
          title: 'Fel uppstod',
          message: err.message,
          type: 'error'
        });
      }
    } finally {
      setSending(false);
    }
  };

  return {
    sending,
    handleSendOffert,
  };
};

export default useOffertActions;

